/** «Перевести» — inline button рядом с uz/en полями.
 *
 * UX: click → POST /admin/v1/translate с source-text → setValue(target).
 * Disabled пока source пустой. Spinner во время запроса.
 *
 * Использование:
 *   <TranslateButton
 *     sourceText={ruName}
 *     targetLang="uz"
 *     onTranslated={(text) => setValue('translations.uz.name', text)}
 *   />
 */

import { useMutation } from "@tanstack/react-query";
import { Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

interface Props {
  sourceText: string;
  sourceLang?: "ru" | "uz" | "en";
  targetLang: "uz" | "en";
  onTranslated: (text: string) => void;
  size?: "sm" | "icon";
  className?: string;
}

export function TranslateButton({
  sourceText,
  sourceLang = "ru",
  targetLang,
  onTranslated,
  size = "sm",
  className,
}: Props): JSX.Element {
  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ translated: string }>(
        "/translate",
        {
          text: sourceText.trim(),
          source: sourceLang,
          target: targetLang,
        },
      );
      return data.translated;
    },
    onSuccess: (text) => {
      onTranslated(text);
      toast.success(`Переведено на ${targetLang}`);
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.error?.message ?? "Не удалось перевести",
      ),
  });

  const disabled = !sourceText.trim() || mutation.isPending;

  return (
    <Button
      type="button"
      size={size}
      variant="outline"
      disabled={disabled}
      onClick={() => mutation.mutate()}
      className={className}
      title={`Перевести из ${sourceLang.toUpperCase()} в ${targetLang.toUpperCase()}`}
    >
      {mutation.isPending ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Languages className="w-3 h-3" />
      )}
      {size !== "icon" && (
        <span className="ml-1 text-xs">из {sourceLang.toUpperCase()}</span>
      )}
    </Button>
  );
}
